import { completeVoucherifyTask, voucherifyGetVoucher, voucherifyValidateVoucher } from '../util/api';
import { storableError } from '../util/errors';
import { logError } from '../util/log';
import { fetchCurrentUser } from './user.duck';

// ================ Action types ================ //

export const VOUCHER_VALIDATE_REQUEST = 'app/Voucherify/VOUCHER_VALIDATE_REQUEST';
export const VOUCHER_VALIDATE_SUCCESS = 'app/Voucherify/VOUCHER_VALIDATE_SUCCESS';
export const VOUCHER_VALIDATE_ERROR = 'app/Voucherify/VOUCHER_VALIDATE_ERROR';

export const ANNUAL_PASS_VALIDATE_SUCCESS = 'app/Voucherify/ANNUAL_PASS_VALIDATE_SUCCESS';

export const COMPLETE_TASK_REQUEST = 'app/Voucherify/COMPLETE_TASK_REQUEST';
export const COMPLETE_TASK_SUCCESS = 'app/Voucherify/COMPLETE_TASK_SUCCESS';
export const COMPLETE_TASK_ERROR = 'app/Voucherify/COMPLETE_TASK_ERROR';

export const GET_VOUCHER_REQUEST = 'app/Voucherify/GET_VOUCHER_REQUEST';
export const GET_VOUCHER_SUCCESS = 'app/Voucherify/GET_VOUCHER_SUCCESS';
export const GET_VOUCHER_ERROR = 'app/Voucherify/GET_VOUCHER_ERROR';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.Voucherify} */
const initialState = {
  voucher: null,
  annualPassVoucher: null,
  voucherValidationInProgress: false,
  voucherValidationError: null,
  voucherifyCustomer: null,
  completeTaskInProgress: false,
  completeTaskError: null,
  getVoucherInProgress: false,
  getVoucherError: null,
};

/**
 *
 * @param state
 * @param action
 */
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case VOUCHER_VALIDATE_REQUEST:
      return { ...state, voucherValidationInProgress: true, voucherValidationError: null };
    case VOUCHER_VALIDATE_SUCCESS:
      return {
        ...state,
        voucher: payload.voucher,
        voucherValidationInProgress: false,
      };
    case ANNUAL_PASS_VALIDATE_SUCCESS:
      return {
        ...state,
        annualPassVoucher: payload.annualPassVoucher,
        voucherValidationInProgress: false,
      };
    case VOUCHER_VALIDATE_ERROR:
      return { ...state, voucherValidationInProgress: false, voucherValidationError: payload };

    case COMPLETE_TASK_REQUEST:
      return { ...state, completeTaskInProgress: true, completeTaskError: null };
    case COMPLETE_TASK_SUCCESS:
      return { ...state, completeTaskInProgress: false };
    case COMPLETE_TASK_ERROR:
      return { ...state, completeTaskInProgress: false, completeTaskError: payload };

    case GET_VOUCHER_REQUEST:
      return { ...state, getVoucherInProgress: true, getVoucherError: null };
    case GET_VOUCHER_SUCCESS:
      return { ...state, getVoucherInProgress: false, voucher: payload };
    case GET_VOUCHER_ERROR:
      return { ...state, getVoucherInProgress: false, getVoucherError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const TASK_NAMES = {
  APP_RATED: 'app_rated',
  PROFILE_FILLED_OUT: 'profile_filled_out',
  REVIEW_LEFT: 'review_left',
};

// ================ Action creators ================ //

export const voucherValidateRequest = () => ({ type: VOUCHER_VALIDATE_REQUEST });
export const voucherValidateSuccess = (payload) => ({
  type: VOUCHER_VALIDATE_SUCCESS,
  payload,
});
export const annualPassValidateSuccess = (payload) => ({
  type: ANNUAL_PASS_VALIDATE_SUCCESS,
  payload,
});
export const voucherValidateFailed = (error) => ({
  type: VOUCHER_VALIDATE_ERROR,
  payload: error,
  error: true,
});

export const completeTaskRequest = () => ({ type: COMPLETE_TASK_REQUEST });
export const completeTaskSuccess = (payload) => ({
  type: COMPLETE_TASK_SUCCESS,
  payload,
});
export const completeTaskError = (error) => ({
  type: COMPLETE_TASK_ERROR,
  payload: error,
});

export const getVoucherRequest = () => ({ type: GET_VOUCHER_REQUEST });
export const getVoucherSuccess = (payload) => ({
  type: GET_VOUCHER_SUCCESS,
  payload,
});
export const getVoucherError = (error) => ({
  type: GET_VOUCHER_ERROR,
  payload: error,
});

// ================ Thunks ================ //

/**
 * Completes a task for the current user.
 * @type {(taskName: string, delay?: number) => import('../../types/vansite').AppThunkAction<Promise<any>>}
 */
export const completeTask = (taskName) => async (dispatch, getState) => {
  if (getState().voucherify.completeTaskInProgress) {
    return;
  }

  dispatch(completeTaskRequest());
  const { currentUser } = getState().user;

  const memberCode = currentUser.attributes.profile.privateData.loyalty?.memberCode;

  if (!memberCode) {
    dispatch(completeTaskSuccess());
    return;
  }

  try {
    const voucherifyCustomerId = currentUser.attributes.profile.privateData.voucherifyCustomer?.id;
    const response = await completeVoucherifyTask({
      eventName: taskName,
      voucherifyCustomerId,
      memberCode,
    });
    dispatch(completeTaskSuccess(response));
    dispatch(fetchCurrentUser());
    return response;
  } catch (error) {
    dispatch(completeTaskError(error));
  }
};

export const getVoucher = (params) => async (dispatch, getState) => {
  const { code } = params;

  if (getState().voucherify.getVoucherInProgress) {
    return;
  }

  dispatch(getVoucherRequest());

  try {
    const response = await voucherifyGetVoucher({ code });
    dispatch(getVoucherSuccess(response.data));
    return response.data;
  } catch (error) {
    dispatch(getVoucherError(error));
  }
};

/**
 * True when a voucher validation failed because the code simply does not
 * exist. Both failure channels of `validateVoucher` land here: the thrown HTTP
 * error (util/api spreads the body onto the Error and `storableError` keeps
 * `status`/`data`, so `not_found` sits at `e.data.key` with `status` 404) and
 * a resolved `{ error }` body carrying Voucherify's own `{ code: 404 }`.
 * A mistyped code is a normal user outcome, not a bug — callers use this to
 * pick the message and the duck uses it to keep the case out of Sentry.
 *
 * @param {any} failure thrown error or resolved `{ error }` response
 * @returns {boolean}
 */
export const isVoucherNotFound = (failure) =>
  failure?.data?.key === 'not_found' ||
  failure?.status === 404 ||
  failure?.error?.code === 404 ||
  failure?.code === 404;

export const validateVoucher = (params) => (dispatch, getState) => {
  dispatch(voucherValidateRequest());
  const { code, lineItems = [] } = params;
  const { currentUser } = getState().user;
  const voucherifyCustomer = currentUser?.attributes?.profile?.privateData?.voucherifyCustomer;

  return voucherifyValidateVoucher({
    code,
    lineItems,
    params: voucherifyCustomer ? { customer: { id: voucherifyCustomer.id } } : {},
  })
    .then((response) => {
      const { data } = response;

      if (data.error) {
        dispatch(voucherValidateFailed(data.error));
        return data;
      }
      dispatch(voucherValidateSuccess({ voucher: { ...data } }));
      return data;
    })
    .catch((err) => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(voucherValidateFailed(e));

      const containsResponse = err.error;
      // Log error
      const { code, valid, reason, tracking_id } = containsResponse ? err.error : {};
      const loggableError = containsResponse
        ? {
          code,
          valid,
          reason,
          tracking_id,
        }
        : e;
      // A code that does not exist is the customer's typo, not our bug —
      // reporting it filled Sentry with one issue per mistyped voucher
      // (VANSITE-FRONTEND-4PR). Everything else stays visible.
      if (!isVoucherNotFound(err)) {
        logError(loggableError, 'voucher-validate-voucher-failed', {
          stripeMessage: loggableError.message,
        });
      }
      throw e;
    });
};
